import { Injectable } from '@angular/core';
import { driver, Config } from 'driver.js';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class HelpService {
  private currentTour: any = null;
  activeStep$ = new Subject<string | null>();
  constructor() {}

  /**
   * Inicia un tour de ayuda con los pasos proporcionados
   * @param steps Array de pasos para el tour
   * @param title Título del tour (opcional)
   */
  startTour(steps: any[], title?: string): void {
    if (this.currentTour) {
      this.currentTour.destroy();
    }

    // Filtrar pasos que no cumplan con la condición
    const filteredSteps = steps.filter(step => {
      if (step.condition && typeof step.condition === 'function') {
        return step.condition();
      }
      // Si no hay condición, verificar que el elemento existe en el DOM
      if (step.element) {
        return document.querySelector(step.element) !== null;
      }
      return true;
    });

    const config: Config = {
      allowClose: true,
      showProgress: true,
      doneBtnText: 'Finalizar',
      nextBtnText: 'Siguiente',
      prevBtnText: 'Anterior',
      steps: filteredSteps,
    };

    this.currentTour = driver(config);
    this.currentTour.drive();
  }

  /**
   * Detiene el tour actual
   */
  stopTour(): void {
    if (this.currentTour) {
      this.currentTour.destroy();
      this.currentTour = null;
    }
  }

  /**
   * Define los pasos del tour para la pantalla principal (Monitores)
   */
  getMonitoresHelpSteps(): any[] {
    return [
      {
        element: '.sidebar-container',
        popover: {
          title: '🧭 Panel lateral',
          description:
            'Contiene la lista de monitores, accesos a usuarios y ajustes. Selecciona un monitor para cargar sus alarmas y configuración en el área principal.',
          side: 'right',
          align: 'start',
        },
      },

      {
        element: '.monitors-table',
        popover: {
          title: '📍 Lista de Monitores',
          description:
            'Muestra todos los monitores disponibles. Haz clic en el nombre de un monitor para seleccionarlo y ver su panel de alarmas y detalles.',
          side: 'left',
          align: 'start',
        },
      },

      {
        element: '.user-table',
        popover: {
          title: '👥 Gestión de usuarios',
          description:
            'Acceso directo a la gestión de usuarios. Visible solo para administradores: abre la pantalla de gestión al hacer clic.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.config_moitors',
        popover: {
          title: '⚙️ Configuración de Monitores',
          description:
            'Entra a las opciones de configuración del monitor seleccionado: colores de eventos, parámetros y reglas específicas.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.logout_section',
        popover: {
          title: '🔒 Cerrar sesión',
          description:
            'Cierra tu sesión de forma segura. Úsalo cuando termines tu trabajo para proteger el acceso al sistema.',
          side: 'left',
          align: 'start',
        },
      },

      {
        element: '.table-container',
        popover: {
          title: '⚠️ Panel de Alarmas',
          description:
            'Aquí se listan las alarmas del monitor seleccionado. Usa filtros, orden y paginación para localizar eventos relevantes.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '.button-logout',
        popover: {
          title: '🔒 Cerrar sesión',
          description:
            'Cierra tu sesión de forma segura. Úsalo cuando termines tu trabajo para proteger el acceso al sistema.',
          side: 'left',
          align: 'start',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para la tabla de alarmas
   */
  getAlarmsTableHelpSteps(): any[] {
    return [
      {
        element: '.mute-container',
        popover: {
          title: '🔊 Control de Sonido',
          description:
            'Activa o desactiva las notificaciones sonoras cuando lleguen nuevas alarmas. Úsalo para trabajar en silencio o para mantener alertas activas.',
          side: 'bottom',
          align: 'start',
        },
      },

      {
        element: '.monitorButton-container',
        popover: {
          title: '📊 Cambiar Modo de Vista',
          description:
            'Alterna entre "Monitor Activo" (alarmas en tiempo real) e "Histórico Activo" (registro de alarmas del día). Solo disponible para administradores.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '.table-container',
        popover: {
          title: '📋 Tabla de Alarmas',
          description:
            'Visualiza todas las alarmas del monitor seleccionado. Cada fila muestra: nombre del activo, placa, tipo de evento, ubicación, fecha/hora y estado.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '.help-header-table',
        popover: {
          title: '🚗 Encabezados de Tabla',
          description:
            'Cada columna contiene información importante: Nombre del activo, Placa, Tipo de evento, Dirección, Fecha y Estado de atención.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-header-tr',
        popover: {
          title: '🚗 Evento',
          description:
            'La columna contiene información importante: Nombre del activo, Placa, Tipo de evento, Dirección, Fecha y Estado de atención. Seleccione una fila para poder ver los detalles de la alarma.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-holder',
        popover: {
          title: '🚗 Nombre del Activo',
          description:
            'Muestra el nombre del vehículo o activo monitorizado. Las alarmas nuevas aparecen con una etiqueta "NUEVO" para identificarlas fácilmente.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-deviceId',
        popover: {
          title: '🏷️ Placa del Vehículo',
          description:
            'Muestra la placa o identificación del vehículo. Útil para identificar rápidamente el activo involucrado en la alarma.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-event',
        popover: {
          title: '⚡ Tipo de Evento',
          description:
            'Describe qué tipo de alarma se generó: velocidad excesiva, conducción anómala, salida de ruta, etc.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-direccion',
        popover: {
          title: '📍 Dirección',
          description:
            'Muestra la ubicación aproximada donde se generó la alarma. Haz clic para ver detalles en el mapa.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-timestamp',
        popover: {
          title: '⏰ Fecha y Hora',
          description:
            'Indica cuándo se registró la alarma. Permite filtrar eventos por rango de tiempo.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-status',
        popover: {
          title: '✅ Estado de Atención',
          description:
            'Marca si la alarma fue atendida o no y quién está revisándola. Las alarmas no atendidas requieren acción inmediata.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: 'mat-paginator',
        popover: {
          title: '📄 Paginación',
          description:
            'Navega entre páginas de alarmas. Puedes cambiar cuántos registros mostrar por página (10, 25, 50, etc.).',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para los filtros de histórico
   */
  getHistoricoFiltersHelpSteps(): any[] {
    return [
      {
        element: '.filters-container',
        popover: {
          title: '🔍 Filtros de Búsqueda',
          description:
            'Usa estos filtros para buscar alarmas específicas por rango de fechas, usuario que clasificó o usuario que atendió.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: 'mat-form-field',
        popover: {
          title: '📅 Rango Rápido',
          description:
            'Selecciona un rango predefinido (últimos 7 días, mes, 3 meses) para filtrar rápidamente.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.filter-fecha-desde',
        popover: {
          title: '📅 Fecha Inicial',
          description:
            'Selecciona la fecha de inicio para filtrar alarmas desde esta fecha en adelante.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.filter-fecha-hasta',
        popover: {
          title: '📅 Fecha Final',
          description:
            'Selecciona la fecha de fin para filtrar alarmas hasta esta fecha.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.filter-clasificado-por',
        popover: {
          title: '👤 Clasificado Por',
          description:
            'Filtra alarmas por el usuario que realizó la clasificación de la alarma.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '.filter-btn',
        popover: {
          title: '✅ Aplicar Filtros',
          description:
            'Haz clic aquí después de establecer tus filtros para aplicarlos y ver los resultados.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '.clear-filter-btn',
        popover: {
          title: '✅ Limpiar Filtros',
          description:
            'Haz clic aquí después de establecer tus filtros para aplicarlos y ver los resultados.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '.help-header-table',
        popover: {
          title: '🚗 Encabezados de Tabla',
          description:
            'Cada columna contiene información importante: Nombre del activo, Placa, Tipo de evento, Dirección, Fecha y Estado de atención.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-header-tr',
        popover: {
          title: '🚗 Evento',
          description:
            'La columna contiene información importante: Nombre del activo, Placa, Tipo de evento, Dirección, Fecha y Estado de atención. Seleccione una fila para poder ver los detalles de la alarma.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-holder',
        popover: {
          title: '🚗 Nombre del Activo',
          description:
            'Muestra el nombre del vehículo o activo monitorizado. Las alarmas nuevas aparecen con una etiqueta "NUEVO" para identificarlas fácilmente.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-deviceId',
        popover: {
          title: '🏷️ Placa del Vehículo',
          description:
            'Muestra la placa o identificación del vehículo. Útil para identificar rápidamente el activo involucrado en la alarma.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-event',
        popover: {
          title: '⚡ Tipo de Evento',
          description:
            'Describe qué tipo de alarma se generó: velocidad excesiva, conducción anómala, salida de ruta, etc.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-direccion',
        popover: {
          title: '📍 Dirección',
          description:
            'Muestra la ubicación aproximada donde se generó la alarma. Haz clic para ver detalles en el mapa.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-timestamp',
        popover: {
          title: '⏰ Fecha y Hora',
          description:
            'Indica cuándo se registró la alarma. Permite filtrar eventos por rango de tiempo.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-atendio',
        popover: {
          title: '👤 Clasificado Por',
          description:
            'Muestra el nombre del usuario que clasificó la alarma y cuándo lo hizo.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-reviso',
        popover: {
          title: '👤 Atendido Por',
          description:
            'Muestra el nombre del usuario que atendió la alarma y cuándo lo hizo.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-comentarios',
        popover: {
          title: '💬 Comentarios',
          description:
            'Muestra los comentarios o observaciones agregadas durante la clasificación o atención de la alarma.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.help-cell-status',
        popover: {
          title: '✅ Estado de Atención',
          description:
            'Marca si la alarma fue atendida o no y quién está revisándola. Las alarmas no atendidas requieren acción inmediata.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: 'mat-paginator',
        popover: {
          title: '📄 Paginación',
          description:
            'Navega entre páginas de alarmas. Puedes cambiar cuántos registros mostrar por página (10, 25, 50, etc.).',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para detalles de alarma
   */
  getAlarmDetailsHelpSteps(): any[] {
    return [
      {
        element: '.alarm-details-content',
        popover: {
          title: '🔍 Detalles de Alarma',
          description:
            'Visualiza toda la información sobre la alarma seleccionada: ubicación, datos básicos, historial y opciones de clasificación.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.location-info-card',
        popover: {
          title: '📍 Mapa de Ubicación',
          description:
            'El mapa interactivo muestra la ubicación exacta del activo donde se generó la alarma. Puedes hacer zoom y moverte para ver más detalles.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.basic-info-card',
        popover: {
          title: '📋 Información Básica',
          description:
            'Aquí encontrarás datos clave: nombre del activo, tipo de evento, fecha/hora de la alarma y nivel de riesgo.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.classification-form',
        popover: {
          title: '✅ Clasificación de Alarma',
          description:
            'Clasifica la alarma seleccionando un estado y agregando observaciones. Esto ayuda a mejorar el análisis de eventos.',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para Clasificacin de alarma por lotes
   */
  getAlarmClasificHelpSteps(): any[] {
    return [
      {
        element: '.alarm-details-content',
        popover: {
          title: '🔍 Detalles de Alarma',
          description:
            'Visualiza toda la información sobre la alarma seleccionada: ubicación, datos básicos, historial y opciones de clasificación.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.filters-container',
        popover: {
          title: '✅ Clasificación de Alarma',
          description:
            'Clasifica la alarma seleccionando un estado y agregando observaciones. Esto ayuda a mejorar el análisis de eventos.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '.selection-header',
        popover: {
          title: '✅ Clasificación de Alarma',
          description:
            'Clasifica la alarma seleccionando un estado y agregando observaciones. Esto ayuda a mejorar el análisis de eventos.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '.alarm-checkbox-list',
        popover: {
          title: '✅ Clasificación de Alarma',
          description:
            'Clasifica la alarma seleccionando un estado y agregando observaciones. Esto ayuda a mejorar el análisis de eventos.',
          side: 'top',
          align: 'center',
        },
      },

      {
        element: '.classification-form',
        popover: {
          title: '✅ Clasificación de Alarma',
          description:
            'Clasifica la alarma seleccionando un estado y agregando observaciones. Esto ayuda a mejorar el análisis de eventos.',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para formulario de clasificación
   */
  getAlarmClassificationFormHelpSteps(): any[] {
    return [
      {
        element: '.classification-form-container',
        popover: {
          title: '📝 Formulario de Clasificación',
          description:
            'Complete este formulario para clasificar la alarma. Selecciona un estado del listado y escribe tus observaciones.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: 'select[formControlName="status"]',
        popover: {
          title: '🏷️ Seleccionar Estado',
          description:
            'Elige el estado que mejor describe la alarma. Los estados disponibles incluyen: Alarma Verdadero, Falso Positivo, Pendiente, etc.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: 'textarea[formControlName="observations"]',
        popover: {
          title: '💬 Agregar Observaciones',
          description:
            'Escribe cualquier nota adicional que ayude a entender la naturaleza de la alarma o decisión tomada.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.form-actions',
        popover: {
          title: '🔘 Enviar o Cancelar',
          description:
            'Usa "Enviar Clasificación" para guardar tu decisión o "Cancelar" para descartar los cambios.',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para detalles de monitor (admin)
   */
  getMonitorDetailsHelpSteps(): any[] {
    return [
      {
        element: '.monitors-details-container',
        popover: {
          title: '⚙️ Detalles del Monitor',
          description:
            'Aquí puedes editar configuración del monitor: nombre, descripción, tipos de eventos y geocercas.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.monitor-select',
        popover: {
          title: '📝 Información del Monitor',
          description:
            'Modifica los datos básicos del monitor. Los cambios se guardan automáticamente.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.mat-mdc-tab-header',
        popover: {
          title: '📝 Información del Monitor',
          description:
            'Modifica los datos básicos del monitor. Los cambios se guardan automáticamente.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.alarmas-content',
        popover: {
          title: '🎨 Colores de Eventos',
          description:
            'Personaliza los colores que se muestran para cada tipo de evento en el mapa y tablas.',
          side: 'left',
          align: 'start',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para gestión de usuarios (admin)
   */
  getUsersManagementHelpSteps(): any[] {
    return [
      {
        element: '.users-table',
        popover: {
          title: '👥 Gestión de Usuarios',
          description:
            'Aquí puedes ver y gestionar todos los usuarios del sistema. Puedes crear nuevos usuarios, editar permisos y desactivar accesos.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.users-table',
        popover: {
          title: '🔧 Acciones de Usuario',
          description:
            'Usa estos botones para editar, eliminar o cambiar los permisos de cada usuario.',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para detalles de usuario
   */
  getUsersDetailsHelpSteps(): any[] {
    return [
      {
        element: '.users-details-container',
        popover: {
          title: '👥 Detalles de Usuario',
          description:
            'Gestiona información de usuarios: crear, editar, asignar roles y cambiar permisos.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.users-table-wrapper',
        popover: {
          title: '📝 Formulario de Usuario',
          description:
            'Completa o edita los datos del usuario: nombre, email, rol, estado de activación.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.mat-column-actions',
        popover: {
          title: '🔐 Permisos de Usuario',
          description:
            'Usa los toggles para habilitar/deshabilitar.',
          side: 'left',
          align: 'start',
        },
      },{
        element: '.user-add-button',
        popover: {
          title: '🔐 Permisos de Usuario',
          description:
            'Usa los toggles para habilitar/deshabilitar.',
          side: 'left',
          align: 'start',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para el mapa interactivo
   */
  getMapHelpSteps(): any[] {
    return [
      {
        element: '.map-container',
        popover: {
          title: '🗺️ Mapa Interactivo',
          description:
            'El mapa muestra la ubicación de tus activos y alarmas. Usa el ratón para mover y la rueda para hacer zoom.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '.leaflet-control-zoom',
        popover: {
          title: '🔍 Controles de Zoom',
          description:
            'Usa los botones + y - para hacer zoom. También puedes usar la rueda del ratón o los gestos en dispositivos móviles.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '.leaflet-control-attribution',
        popover: {
          title: '📍 Fuente del Mapa',
          description:
            'Este mapa está basado en OpenStreetMap. Puedes hacer clic en los marcadores para ver información del activo.',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para alertas/notificaciones
   */
  getAlertHelpSteps(): any[] {
    return [
      {
        element: '.alert-container',
        popover: {
          title: '📢 Notificaciones de Sistema',
          description:
            'Estos mensajes informan sobre el estado de tus acciones: éxito, error, advertencia o información. Se cierran automáticamente.',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }

  /**
   * Define los pasos del tour para diálogo de confirmación
   */
  getConfirmDialogHelpSteps(): any[] {
    return [
      {
        element: '.confirm-dialog-content',
        popover: {
          title: '❓ Diálogo de Confirmación',
          description:
            'Lee el mensaje cuidadosamente. Esta ventana te pide que confirmes una acción importante.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '.dialog-actions',
        popover: {
          title: '✅ Confirmar o Cancelar',
          description:
            'Haz clic en "Confirmar" para proceder con la acción o "Cancelar" para descartar.',
          side: 'top',
          align: 'center',
        },
      },
    ];
  }
}
